<template>
  <div class="zdraveto-page">
    <div class="nav-bg">
      <div class="nav-wrap">
        <NavigationBar />
      </div>
    </div>

    <!-- Hero Section (full-width split) -->
    <section class="hero-full">
      <div class="hero-full__grid">
        <div class="hero-full__copy">
          <div class="hero-eyebrow">
            <span class="hero-eyebrow__dot"></span>
            <span>{{ $t('zdraveto-hero-eyebrow') }}</span>
          </div>
          <h1 class="hero-title text-style-home-heading">{{ $t('zdraveto-hero-title') }}</h1>
          <p class="hero-subtitle">{{ $t('zdraveto-hero-subtitle') }}</p>

          <div class="hero-actions">
            <button class="btn-pink" type="button" @click="goToDownload">
              {{ $t('zdraveto-cta-btn') }}
            </button>
            <button class="btn-ghost" type="button" @click="scrollToInitiative">
              {{ $t('zdraveto-learn-more') }}
            </button>
          </div>
        </div>

        <div class="hero-full__video">
          <div class="video-container">
            <button
              v-if="!isVideoPlaying"
              class="video-thumb"
              type="button"
              aria-label="Play video"
              @click="isVideoPlaying = true"
            >
              <img src="/static/File_000.png" alt="" />
              <span class="video-thumb__play" aria-hidden="true"></span>
            </button>
            <iframe
              v-else
              :src="`https://www.youtube.com/embed/${$t('zdraveto-video-id')}?autoplay=1`"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            ></iframe>
            <div v-if="!isVideoPlaying" class="video-chip video-chip--logo">
              <img src="/static/logo/zdravetoyellow.png" alt="Zdraveto Hospital" class="zdraveto-logo" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Promo Ticket Band -->
    <section class="promo-band container">
      <h2 class="promo-band__heading">{{ $t('zdraveto-promo-heading') }}</h2>

      <div class="promo-ticket">
        <div class="promo-ticket__inner">
          <div class="promo-ticket__text">
            <div class="promo-ticket__label">{{ $t('zdraveto-promo-label') }}</div>
            <div class="promo-ticket__code">{{ $t('zdraveto-promo-code-value') }}</div>
            <div class="promo-ticket__subtitle" v-html="$t('zdraveto-promo-subtitle')"></div>
          </div>
          <button class="promo-ticket__copy" type="button" @click="copyPromo">
            {{ copied ? $t('zdraveto-promo-copied') : $t('zdraveto-promo-copy') }}
          </button>
        </div>
        <span class="promo-ticket__notch promo-ticket__notch--left"></span>
        <span class="promo-ticket__notch promo-ticket__notch--right"></span>
      </div>

      <div class="promo-disclaimer-box">
        <div class="promo-notes">
          <p class="promo-note" v-html="$t('zdraveto-promo-note-1')"></p>
          <p class="promo-note" v-html="$t('zdraveto-promo-note-2')"></p>
        </div>
      </div>
    </section>

    <!-- Initiative Q&A -->
    <section id="initiative" ref="initiativeRef" class="initiative container">
      <div class="initiative-grid">
        <div class="initiative-accordion">
          <div class="initiative-accordion__header">
            <h2 class="initiative-aside__title text-style-home-heading">{{ $t('zdraveto-initiative-title') }}</h2>
          </div>
          <div
            v-for="(qa, i) in qaItems"
            :key="i"
            class="accordion-item"
            :class="{ 'accordion-item--open': open === i }"
          >
            <button class="accordion-trigger" type="button" @click="toggle(i)">
              <h4 class="accordion-title">{{ $t(qa.titleKey) }}</h4>
              <span class="accordion-icon">{{ open === i ? '−' : '+' }}</span>
            </button>
            <div v-if="open === i" class="accordion-body">
              <p class="accordion-text" v-html="$t(qa.textKey)"></p>
              <ul v-if="qa.bulletKeys" class="accordion-list">
                <li v-for="(bk, j) in qa.bulletKeys" :key="j">{{ $t(bk) }}</li>
              </ul>
              <p v-if="qa.footerKey" class="accordion-text accordion-text--footer">
                {{ $t(qa.footerKey) }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="initiative-photos">
        <figure class="initiative-photo">
          <img src="/static/zdraveto/initiative-photo-2.png" alt="" />
        </figure>
        <figure class="initiative-photo">
          <img src="/static/zdraveto/initiative-photo.png" alt="" />
        </figure>
      </div>
    </section>

    <!-- App Support (inverse pink band) -->
    <section class="app-support">
      <div class="app-support__leaf" aria-hidden="true"></div>
      <div class="container">
        <h2 class="app-support__title">{{ $t('zdraveto-app-support-title') }}</h2>
        <div class="app-support__grid">
          <div class="app-support__phone">
            <img src="/static/momfident_phone_screen.png" alt="Momfident App" />
          </div>
          <div class="app-support__features">
            <div v-for="(f, i) in features" :key="i" class="feature-row">
              <div class="feature-row__num">0{{ i + 1 }}</div>
              <div class="feature-row__body">
                <h4 class="feature-row__title">{{ $t(f.titleKey) }}</h4>
                <p class="feature-row__text">{{ $t(f.textKey) }}</p>
              </div>
            </div>

            <div class="app-support__actions">
              <button class="btn-white" type="button" @click="goToDownload">
                {{ $t('zdraveto-cta-btn') }}
              </button>
              <div class="store-badges">
                <a href="https://apps.apple.com/us/app/momfident/id6751100834" target="_blank" rel="noopener noreferrer">
                  <AppStore />
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.momfident.app" target="_blank" rel="noopener noreferrer">
                  <GooglePlay />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials -->
    <section class="reviews container">
      <div class="reviews__header">
        <div class="reviews__eyebrow">{{ $t('zdraveto-reviews-eyebrow') }}</div>
        <h2 class="reviews__title text-style-home-heading" v-html="$t('zdraveto-reviews-title')"></h2>
      </div>
      <HomeTestimonialsComponent />
    </section>

    <!-- Footer -->
    <div class="footer-wrap">
      <FooterComponent :show-logos="false" />
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import NavigationBar from '@/components/NavigationBar.vue';
import FooterComponent from '@/components/Components/FooterComponent.vue';
import HomeTestimonialsComponent from '@/components/Components/Home/HomeTestimonialsComponent.vue';
import AppStore from '@/components/Common/Buttons/AppStore.vue';
import GooglePlay from '@/components/Common/Buttons/GooglePlay.vue';

const { t, locale } = useI18n();
const router = useRouter();

const open = ref(0);
const copied = ref(false);
const initiativeRef = ref(null);
const isVideoPlaying = ref(false);

const qaItems = [
  { titleKey: 'zdraveto-qa-1-title', textKey: 'zdraveto-qa-1-text' },
  {
    titleKey: 'zdraveto-qa-2-title',
    textKey: 'zdraveto-qa-2-text',
    bulletKeys: ['zdraveto-qa-2-bullet-1', 'zdraveto-qa-2-bullet-2'],
    footerKey: 'zdraveto-qa-2-footer',
  },
  { titleKey: 'zdraveto-qa-3-title', textKey: 'zdraveto-qa-3-text' },
  { titleKey: 'zdraveto-qa-4-title', textKey: 'zdraveto-qa-4-text' },
];

const features = [
  { titleKey: 'zdraveto-feature-1-title', textKey: 'zdraveto-feature-1-text' },
  { titleKey: 'zdraveto-feature-2-title', textKey: 'zdraveto-feature-2-text' },
  { titleKey: 'zdraveto-feature-3-title', textKey: 'zdraveto-feature-3-text' },
];

function toggle(i) {
  open.value = open.value === i ? -1 : i;
}

function goToDownload() {
  const targetRoute = locale.value === 'en' ? 'download-en' : 'download';
  router.push({ name: targetRoute });
}

function scrollToInitiative() {
  initiativeRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

async function copyPromo() {
  await navigator.clipboard.writeText(t('zdraveto-promo-code-value'));
  copied.value = true;
  setTimeout(() => (copied.value = false), 1800);
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/fonts.scss";
@import "~@/assets/scss/variables.scss";

/* ==================== BASE ==================== */
.text-style-home-heading {
  font-family: $font-thin;
  font-weight: 300;
  line-height: 1.08;
  color: $color-purple;
  letter-spacing: -0.5px;
}

.zdraveto-page {
  background: #FFEFF2;
  color: $color-purple;
  font-family: 'Rubik', sans-serif;
  overflow-x: hidden;
}

.nav-bg {
  background: #ffffff;
  padding: 16px 0;
}

/* NavigationBar has outer vertical margins (my-4); remove so white fills edge-to-edge */
.nav-bg :deep(.my-4) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.nav-wrap {
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 8px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
}

/* ==================== HERO (FULL-WIDTH SPLIT, FLAT) ==================== */
.hero-full {
  width: 100%;
  background: #fff;
  margin-top: 0;
}

.hero-full__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 64px;
  align-items: center;
  min-height: 640px;
  padding: 80px 64px;
}

.hero-full__copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 620px;
}

.hero-full__video {
  width: 100%;
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #FFEFF2;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 11px;
  color: $color-pink-dark-2;
  font-weight: 600;
  letter-spacing: 1px;
  margin-bottom: 22px;

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: $color-pink-dark-2;
  }
}

.hero-title {
  font-size: 56px;
  line-height: 1.04;
  margin: 0;
  letter-spacing: -1px;
  text-align: left;
}

.hero-subtitle {
  font-size: 18px;
  color: $color-purple;
  opacity: 0.85;
  margin-top: 18px;
  margin-bottom: 28px;
  line-height: 1.5;
  max-width: 520px;
  text-align: left;
}

.partnership-lockup {
  display: inline-flex;
  align-items: center;
  gap: 22px;
  margin-bottom: 28px;

  &__logo {
    height: 36px;
    width: auto;
  }

  &__cross {
    color: #C9B6C7;
    font-size: 22px;
    font-weight: 300;
  }

  &__hospital-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ec8021;
    padding: 8px 16px;
    border-radius: 14px;
  }

  &__hospital {
    height: 32px;
    width: auto;
    display: block;
  }
}

.hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-start;
}

/* ==================== BUTTONS ==================== */
.btn-pink {
  background: linear-gradient(135deg, #FC678B 0%, #EA708C 100%);
  color: #fff;
  border: none;
  padding: 14px 28px;
  border-radius: 25px;
  font-family: 'Rubik-SemiBold', sans-serif;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(252, 103, 139, 0.35);
  transition: transform 0.25s ease, box-shadow 0.25s ease, filter 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(252, 103, 139, 0.45);
    filter: brightness(1.05);
  }
}

.btn-ghost {
  background: transparent;
  color: $color-purple;
  border: 2px solid #E3DEEF;
  padding: 12px 22px;
  border-radius: 25px;
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: transform 0.25s ease, border-color 0.25s ease, color 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: $color-pink-dark-2;
    color: $color-pink-dark-2;
  }
}

.btn-white {
  background: #fff;
  color: $color-pink-dark-2;
  border: none;
  padding: 14px 28px;
  border-radius: 25px;
  font-family: 'Rubik-SemiBold', sans-serif;
  font-weight: 600;
  font-size: 13px;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
  }
}

/* ==================== HERO VIDEO ==================== */
.video-container {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  background: #000;
  border-radius: 20px;
  overflow: hidden;

  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
}

.video-thumb {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;
  display: block;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.video-thumb__play {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;

  &::before {
    content: "";
    width: 86px;
    height: 86px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
    transition: transform 0.25s ease, background 0.25s ease;
  }

  &::after {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    border-left: 18px solid rgba(55, 55, 88, 0.9);
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    transform: translateX(4px);
  }
}

.video-thumb:hover .video-thumb__play::before {
  transform: scale(1.05);
  background: #fff;
}

.video-chip {
  position: absolute;
  z-index: 2;
  pointer-events: none;

  &--logo {
    top: 14px;
    left: 14px;
    background: transparent;
    padding: 0;
    border-radius: 0;

    .zdraveto-logo {
      height: 54px;
      width: auto;
      display: block;
    }
  }

  &--speaker {
    bottom: 14px;
    left: 14px;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(6px);
    color: #fff;
    padding: 8px 14px;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .speaker-name {
      font-family: $font-semi-bold;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.2;
    }

    .speaker-role {
      font-size: 11px;
      opacity: 0.85;
    }
  }
}

/* ==================== PROMO TICKET BAND ==================== */
.promo-band {
  margin-top: 56px;
}

.promo-band__heading {
  font-family: 'Rubik', sans-serif;
  font-weight: 500;
  font-size: 30px;
  color: $color-purple;
  text-align: center;
  max-width: 820px;
  margin: 0 auto 32px;
  line-height: 1.3;
}

.promo-ticket {
  position: relative;
  max-width: 520px;
  margin: 0 auto;
}

.promo-ticket__inner {
  background: linear-gradient(135deg, #FC678B 0%, #EA708C 100%);
  border-radius: 28px;
  padding: 28px 38px;
  color: #fff;
  box-shadow: 0 12px 32px rgba(252, 103, 139, 0.3);
  display: flex;
  align-items: center;
  gap: 20px;
}

.promo-ticket__text {
  flex: 1;
  min-width: 0;
}

.promo-ticket__label {
  font-size: 11px;
  opacity: 0.85;
  letter-spacing: 1.5px;
  font-weight: 600;
  margin-bottom: 6px;
}

.promo-ticket__code {
  font-family: 'JetBrains Mono', ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: 1.5px;
  line-height: 1;
}

.promo-ticket__subtitle {
  font-size: 13px;
  opacity: 0.9;
  margin-top: 8px;
}

.promo-ticket__copy {
  background: #fff;
  color: $color-pink-dark-2;
  border: none;
  border-radius: 999px;
  padding: 12px 20px;
  font-family: $font-bold;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.5px;
  cursor: pointer;
  min-width: 110px;
  white-space: nowrap;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.18);
  }
}

.promo-ticket__notch {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  background: #FFEFF2;
  border-radius: 50%;

  &--left { left: -8px; }
  &--right { right: -8px; }
}

.promo-disclaimer-box {
  background-color: #fefdf7;
  border: 1px solid #d4c4a8;
  border-radius: 15px;
  padding: 16px 18px;
  margin: 22px auto 0;
  max-width: 720px;
  box-shadow: 0 1px 3px rgba(212, 196, 168, 0.2);
}

.promo-notes {
  max-width: 620px;
  margin: 0 auto;
  text-align: center;
}

.promo-note {
  font-size: 16px;
  color: #6b6b9c;
  line-height: 1.6;
  margin: 0 0 6px;

  &:last-child { margin-bottom: 0; }
}

/* ==================== INITIATIVE ==================== */
.initiative {
  margin-top: 100px;
  scroll-margin-top: 20px;
}

.initiative-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
  align-items: flex-start;
}

.initiative-aside__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ec8021;
  padding: 12px 18px;
  border-radius: 16px;
  margin-bottom: 20px;

  img {
    height: 40px;
    width: auto;
    display: block;
  }
}

.initiative-aside__eyebrow {
  font-size: 11px;
  color: $color-pink-dark-2;
  letter-spacing: 1.5px;
  font-weight: 600;
  margin-bottom: 14px;
}

.initiative-aside__title {
  font-size: 44px;
  line-height: 1.05;
  margin: 0;
}


.initiative-accordion__header {
  margin-top: -10px;
  margin-bottom: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}


/* ==================== ACCORDION ==================== */
.accordion-item {
  border-bottom: 1px solid #F0D8DE;
  padding: 22px 0;

  &:first-child { padding-top: 0; }
}

.accordion-trigger {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 100%;
  text-align: left;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  font-family: 'Rubik', sans-serif;
}

.accordion-title {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: $color-purple;
  line-height: 1.35;
  flex: 1;
}

.accordion-icon {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #FFEFF2;
  color: $color-pink-dark-2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 500;
  transition: background 0.3s ease, color 0.3s ease;
}

.accordion-item--open .accordion-icon {
  background: $color-pink-dark-2;
  color: #fff;
}

.accordion-body {
  margin-top: 14px;
}

.accordion-text {
  font-size: 14px;
  line-height: 1.7;
  color: #4A4A4A;
  margin: 0;

  :deep(strong) {
    color: $color-purple;
    font-weight: 600;
  }

  :deep(em) {
    font-style: normal;
    display: inline-block;
    font-weight: 500;
    color: $color-pink-dark-2;
    border-left: 3px solid $color-pink-dark-2;
    padding: 2px 0 2px 12px;
    margin-bottom: 4px;
  }
}

.accordion-text--footer {
  margin-top: 14px;
  font-weight: 500;
  color: $color-purple;
}

.accordion-list {
  margin-top: 12px;
  padding-left: 20px;

  li {
    font-size: 14px;
    line-height: 1.6;
    color: #4A4A4A;
    margin-bottom: 6px;
    font-weight: 500;
  }
}

/* ==================== INITIATIVE PHOTOS ==================== */
.initiative-photos {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 60px;
}

.initiative-photo {
  margin: 0;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px -15px rgba(107, 107, 156, 0.3);
  aspect-ratio: 4 / 3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

/* ==================== APP SUPPORT (INVERSE PINK) ==================== */
.app-support {
  margin-top: 100px;
  background: $color-pink-dark-2;
  padding: 70px 0;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.app-support__leaf {
  position: absolute;
  bottom: -60px;
  right: -80px;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.18), transparent 70%);
  filter: blur(6px);
  pointer-events: none;
}

.app-support__title {
  font-family: 'Rubik', sans-serif;
  font-weight: 300;
  font-size: 40px;
  text-align: center;
  margin: 0 0 44px;
  line-height: 1.1;
  letter-spacing: -0.5px;
}

.app-support__grid {
  display: grid;
  grid-template-columns: 5fr 7fr;
  gap: 56px;
  align-items: center;
  position: relative;
  z-index: 1;
}

.app-support__phone {
  display: flex;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 500px;
    filter: drop-shadow(0 24px 48px rgba(0, 0, 0, 0.25));
  }
}

.feature-row {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  padding: 20px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);

  &:last-of-type { border-bottom: none; }

  &__num {
    font-family: 'Rubik', sans-serif;
    font-weight: 300;
    font-size: 40px;
    opacity: 0.5;
    line-height: 1;
    min-width: 50px;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-family: $font-semi-bold;
    font-weight: 500;
    font-size: 17px;
    margin: 6px 0 8px;
    line-height: 1.3;
  }

  &__text {
    font-size: 13px;
    margin: 0;
    line-height: 1.6;
    opacity: 0.9;
  }
}

.app-support__actions {
  margin-top: 28px;
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
}

.store-badges {
  display: flex;
  gap: 12px;
  align-items: center;

  a {
    display: inline-flex;
    transition: transform 0.2s ease;

    &:hover { transform: translateY(-3px); }
  }

  :deep(svg) {
    height: 44px;
    width: auto;
  }
}

/* ==================== REVIEWS ==================== */
.reviews {
  margin-top: 80px;
}

.reviews__header {
  text-align: center;
  margin-bottom: 40px;
}

.reviews__eyebrow {
  font-size: 11px;
  color: $color-pink-dark-2;
  letter-spacing: 1.5px;
  font-weight: 600;
  margin-bottom: 10px;
}

.reviews__title {
  font-size: 36px;
  margin: 0;
}

.reviews :deep(.testimonials-wrapper) {
  padding: 0;
  background: transparent;
}

.reviews :deep(.testimonials-wrapper > .container) {
  padding: 0;
}

/* ==================== FOOTER ==================== */
.footer-wrap {
  margin-top: 80px;
}

.footer-wrap :deep(.p-5) { display: none !important; }
.nav-wrap :deep(.logo) { margin-left: 10px; }
.nav-wrap :deep(.download-btn) { margin-right: 10px; }

/* ==================== RESPONSIVE ==================== */
@media (max-width: 1200px) {
  .hero-full__grid {
    gap: 40px;
    min-height: 560px;
    padding-block: 64px;
  }
  .hero-title { font-size: 46px; }
}

@media (max-width: 1100px) {
  .initiative-aside__title { font-size: 36px; }
  .app-support__title { font-size: 32px; }
  .initiative-grid { gap: 40px; }
}

@media (max-width: 992px) {
  .container { padding: 0 28px; }

  .hero-full__grid {
    grid-template-columns: 1fr;
    gap: 36px;
    min-height: 0;
    padding-block: 48px;
    padding-inline: 28px;
  }

  .hero-full__copy {
    max-width: 720px;
    margin: 0 auto;
    align-items: center;
    text-align: center;
  }

  .hero-title { font-size: 36px; text-align: center; }
  .hero-subtitle { font-size: 16px; text-align: center; margin-left: auto; margin-right: auto; }

  .hero-actions { justify-content: center; }
  .partnership-lockup { justify-content: center; }

  .initiative-photos { grid-template-columns: 1fr; gap: 18px; margin-top: 44px; }

  .initiative { margin-top: 72px; }

  .initiative-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .app-support { padding: 56px 0; margin-top: 72px; }
  .app-support__title { font-size: 28px; margin-bottom: 36px; }

  .app-support__grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .app-support__phone img { max-height: 360px; }

  .reviews { margin-top: 60px; }
  .reviews__title { font-size: 28px; }
}

@media (max-width: 768px) {
  .nav-wrap { max-width: 100%; padding-left: 12px; padding-right: 12px; }
  .container { padding: 0 16px; }

  .hero-full__grid {
    padding: 36px 16px;
    gap: 28px;
  }

  .hero-eyebrow {
    font-size: 10px;
    padding: 5px 12px;
    letter-spacing: 0.8px;
  }

  .hero-title { font-size: 28px; }
  .hero-subtitle { font-size: 14px; margin-top: 12px; margin-bottom: 20px; }

  .partnership-lockup {
    gap: 14px;
    margin-bottom: 22px;

    &__logo { height: 28px; }
    &__hospital { height: 26px; }
    &__hospital-wrap { padding: 6px 12px; border-radius: 12px; }
    &__cross { font-size: 18px; }
  }

  .initiative-aside__badge {
    padding: 10px 14px;
    margin-bottom: 16px;

    img { height: 32px; }
  }

  .video-container { border-radius: 20px; }
  .video-thumb__play::before { width: 64px; height: 64px; }
  .video-thumb__play::after {
    border-left: 14px solid rgba(55, 55, 88, 0.9);
    border-top: 9px solid transparent;
    border-bottom: 9px solid transparent;
  }

  .btn-pink,
  .btn-white {
    padding: 12px 22px;
    font-size: 12px;
  }

  .btn-ghost {
    padding: 10px 18px;
    font-size: 12px;
  }

  .video-chip--logo {
    top: 10px;
    left: 10px;
    padding: 0;

    .zdraveto-logo { height: 40px; }
  }

  .video-chip--speaker {
    bottom: 10px;
    left: 10px;
    padding: 6px 10px;

    .speaker-name { font-size: 12px; }
    .speaker-role { font-size: 10px; }
  }

  .promo-band { margin-top: 40px; }
  .promo-band__heading { font-size: 22px; margin-bottom: 24px; }

  .promo-ticket__inner {
    flex-direction: column;
    text-align: center;
    gap: 16px;
    padding: 24px 26px;
    border-radius: 22px;
  }

  .promo-ticket__code { font-size: 26px; letter-spacing: 1px; }
  .promo-ticket__copy { width: 100%; }

  .promo-ticket__notch {
    display: none;
  }

  .initiative { margin-top: 56px; }
  .initiative-aside__title { font-size: 28px; }

  .accordion-title { font-size: 16px; }
  .accordion-text { font-size: 13px; }
  .accordion-list li { font-size: 13px; }
  .accordion-icon { width: 26px; height: 26px; font-size: 15px; }

  .app-support { padding: 44px 0; margin-top: 56px; }
  .app-support__title { font-size: 24px; margin-bottom: 28px; }

  .feature-row {
    gap: 14px;
    padding: 16px 0;

    &__num { font-size: 32px; min-width: 42px; }
    &__title { font-size: 15px; }
    &__text { font-size: 12px; }
  }

  .app-support__actions {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .store-badges {
    justify-content: center;

    :deep(svg) { height: 38px; }
  }

  .btn-white { width: 100%; }

  .reviews { margin-top: 48px; }
  .reviews__title { font-size: 24px; }
  .footer-wrap { margin-top: 56px; }
}

@media (max-width: 480px) {
  .hero-title { font-size: 24px; }
  .promo-band__heading { font-size: 19px; }
  .promo-ticket__code { font-size: 22px; }
}
</style>
